
'use strict';

/**
 * Renders the map and all layers
 */

var _ = require('lodash');
var Promise = require('bluebird');

// Renderers
var GeoJsonRenderer = require('./layer/render/GeoJsonRenderer');
var MarkerClusterRenderer = require('./layer/render/MarkerClusterRenderer');

// GeoJson Transform Strategies
var AnnotationTransform = require('./layer/transform/geojson/annotation/Annotation');
var MeasurementTransform = require('./layer/transform/geojson/measurement/Measurement');
var MissionBaseTransform = require('./layer/transform/geojson/mission/base/Base');
var MissionGCPTransform = require('./layer/transform/geojson/mission/gcp/GCP');
var MissionCHPTransform = require('./layer/transform/geojson/mission/chp/CHP');
var MissionObstacleTransform = require('./layer/transform/geojson/mission/obstacle/Obstacle');
var MissionPhotoTransform = require('./layer/transform/geojson/mission/photos/Photos');
var MissionRouteTransform = require('./layer/transform/geojson/mission/route/Route');
var MissionZoneTransform = require('./layer/transform/geojson/mission/zone/Zone');

// MarkerCluster Transform Strategies
var PhotosetTransform = require('./layer/transform/markercluster/photoset/Photoset');

function MapController ($attrs, $scope, Registry, Utils, $rootScope, $timeout) {

  var vm = this;
  var geoJsonRenderer;
  var photosetRenderer;
  var defaults = Utils.setDefaults(vm.mapDefaults);

  _.defaults(this, {
    activeLayer: null,
    annotations: null,
    measurements: null,
    datacaptures: null,
    location: Utils.getDefaultLocation(),
    photoset: null,

    // angular-leaflet properties
    center: Utils.getDefaultLocation(),
    defaults: defaults,
    events: Utils.getEvents(),
    layers: Utils.initLayers(),
    geojson: null,
    geojsonWatchOptions: {doWatch: true},
    markersWatchOptions: {doWatch: false},
    markers: null, // not bound to geojson created layers
    paths: null, // not bound to geojson created layers

    // others
    Actions: null,
    Store: null,
    map: null, // leaflet map instance object
    mapId: null, // user assigned mapId

    // Injected function for getting auth parameters, if present.
    // It can return a value or a promise
    overlayLayerAuthFn: function() {}

  });

  //load', 'layeradd', 'baselayerchange', 'overlayadd

  $scope.$on('leafletDirectiveMap.layeradd', function(){
    $timeout(function() {
      $rootScope.$emit('MAP_LOADED');
    }, 128);
  });

  function init () {
    var Actions, Store;
    var mapId = $attrs.sbMap;
    var registered = Registry.register(mapId);

    vm.mapId = mapId;
    vm.Store = Store = registered.store;
    vm.Actions = Actions = registered.actions;

    initalizeStoreHandlers(Store);

    vm.Store.getMap().then(function (map) {
      var mapOpacity = (vm.Store.get('mapOpacity') / 100) || 1;

      vm.map = map;
      vm.map.eachLayer(function (layer) {
        if (layer._url && layer._url.indexOf('mapbox') > -1) {
          layer.setOpacity(mapOpacity);
        }
      });

      // Is this the correct place to add this?
      // The _ref attribute is orthogonal for annotation layers,
      vm.map.on('layeradd', function(ev) {
        if(ev.layer.options && ev.layer.options._ref) {
          ev.layer._ref = ev.layer.options._ref;
        }
      });

      initializeRenderers(Actions, Store);
      initializeWatchers();

    });

    // TODO: This can be moved to each layer type based on the 'transform' that
    // created it to include the method to generate the hash.
    // Assumes it is an annotation layer for now (the only ones that call this method)
    var getAnnotationLayerRef = function(layer) {
      if (layer.resource === 'measurement') {
        return 'measurement_layer-' + layer.id;
      }
      return 'annotation_layer-' + layer.annotationId + '-' + layer.id;
    };

    vm.refreshLayerStyle = function(layer, styleChanges) {

      var ref = getAnnotationLayerRef(layer);

      vm.map.eachLayer(function(_layer) {
        if(_layer._ref === ref) {
          _layer.setStyle(styleChanges);
          _layer.fire('color:changed');
        }
      });
    };

    var _sanitize = function(text) {
      return _.escape(text);
    };
    vm.refreshLayerText = function(layer, newText) {
      var ref = getAnnotationLayerRef(layer);
      vm.map.eachLayer(function(_layer) {
        if(_layer._ref === ref) {
          // TODO: Move these as strategy function on the separate layer classes
          // instead of making it an `if`
          if(layer.type === 'text') {
            angular.element(_layer._icon.querySelector('.content')).text(newText);
          } else if (layer.type === 'marker' && (newText || newText === '')) { // Allow empty string
            if(!_layer.label) {
              _layer.bindLabel(_sanitize(newText), {
                className : 'sb-map-marker-text',
                noHide : true
              }).showLabel();
            } else {
              if(newText === '') {
                newText = ' '; // Hack: Allow empty strings, leaflet won't change because '' is considered false
              }
              _layer.label.setContent(_sanitize(newText));
            }
          }
        }
      });
    };

    vm.refreshShowMeasurements = function(layer, showMeasurements) {
      var ref = getAnnotationLayerRef(layer);
      vm.map.eachLayer(function(_layer) {
        var layerHasMeasurements = _.includes(
          ['circle', 'polygon', 'rectangle', 'polyline'],
          layer.type
        );
        if(_layer._ref === ref && layerHasMeasurements) {
          if(showMeasurements) {
            _layer.fire('labels:show');
          } else {
            _layer.fire('labels:hide');
          }
        }
      });
    };

    // Works for layers of type `overlay` (no base layers)
    vm.refreshLayerOpacity = function(layerRef, opacity) {
      if(!vm.map) { return; }

      vm.map.eachLayer(function(_layer) {
        if(_layer._ref === layerRef && _layer.setOpacity) {
          _layer.setOpacity(opacity);
        }
      });
    };

    $scope.$on('overlay:opacity:changed', function(ev, layerId, opacity) {
      vm.refreshLayerOpacity(layerId, opacity);
    });
  }

  init();

  //////////////////////////////////////////////////////////////////////////////
  // Renderers
  //////////////////////////////////////////////////////////////////////////////

  function initializeRenderers (Actions, Store) {

    var sharedOptions = {
      map: vm.map,
      Actions: Actions,
      Store: Store
    };

    geoJsonRenderer = GeoJsonRenderer.create(sharedOptions);
    photosetRenderer = MarkerClusterRenderer.create(sharedOptions);
  }

  /**
   * Add new tile layers from "vm.layers.overlays". Also remove old ones.
   *
   * @param  {Object.<Object>} newLayers List of formatted tile layers
   */
  function renderTileLayers (layers, type) {
    // TODO @andre --- submit a fix for this
    // Wrapped in an $applyAsync because angular-leaflet not rendering overlay
    // tiles on first load without it. Probably internally the watchers have not
    // initialized yet
    $scope.$applyAsync(function () {
      vm.layers[type] = layers;
    });
  }

  var renderGeoJSON = _.debounce(function () {
    Promise.resolve(vm.overlayLayerAuthFn()).then(function (options) {
      var annotations, measurements, bases, obstacles, routes, zones, photos, gcps, chps;
      annotations = geoJsonRenderer.using(AnnotationTransform).render(vm.annotations, options);
      measurements = geoJsonRenderer.using(MeasurementTransform).render(vm.measurements, options);

      if (vm.mission) {
        zones = geoJsonRenderer.using(MissionZoneTransform).render(vm.mission);
        bases = geoJsonRenderer.using(MissionBaseTransform).render(vm.mission.zones);
        obstacles = geoJsonRenderer.using(MissionObstacleTransform).render(vm.mission);
        routes = geoJsonRenderer.using(MissionRouteTransform).render(vm.mission.zones);
        photos = geoJsonRenderer.using(MissionPhotoTransform).render(vm.mission.photos);
        gcps = geoJsonRenderer.using(MissionGCPTransform).render(vm.mission.gcps);
        chps = geoJsonRenderer.using(MissionCHPTransform).render(vm.mission.chps);
      }

      vm.geojson = _.extend({}, annotations, measurements, bases, obstacles, zones, routes, photos, gcps, chps);
    });
  }, 10, {leading: true, trailing: false});

  function renderPhotoset () {
    photosetRenderer.using(PhotosetTransform).render(vm.photoset);
  }

  //////////////////////////////////////////////////////////////////////////////
  // Store Handlers
  //////////////////////////////////////////////////////////////////////////////

  function onChangedActiveLayer () {
    vm.activeLayer = vm.Store.get('activeLayer');
  }

  function onEditedActiveLayer () {
    if ($attrs.layerEditFn) {
      var layer = vm.Store.get('activeLayer');
      vm.layerEditFn({layer: layer});
    }
  }

  function initalizeStoreHandlers (store) {
    store.on(store.Events.ChangedActiveLayer, onChangedActiveLayer);
    store.on(store.Events.EditedActiveLayer, onEditedActiveLayer);
  }

  //////////////////////////////////////////////////////////////////////////////
  // Watchers
  //////////////////////////////////////////////////////////////////////////////

  function initializeWatchers () {


    // ## Watchers --- MapBooster ---

    Utils.Ctrl.watch($attrs, $scope, vm, 'editableTypes', function (types) {
      vm.Actions.SetEditableTypes.dispatch(types);
    });

    Utils.Ctrl.watch($attrs, $scope, vm, 'activeLayer', function (layer) {
      if (layer) {
        vm.Actions.SetActiveLayer.dispatch(layer);
      }
    });

    var renderOverlaysDataCaptures = function (overlays, datacaptures, $scope) {
      var dcOpacity = (vm.Store.get('dcOpacity') / 100) || 1;

      if ($attrs.overlayLayerAuthFn) {
        vm.overlayLayerAuthFn().then(function (options) {
          var overlayLayers = Utils.Overlay.getLayers(overlays, options);
          var dcLayers = Utils.DataCapture.getLayers(datacaptures, options);
          // ensure that new datacaptures inherit the opacity that has been set already
          if (dcLayers[Object.keys(dcLayers)[0]]) {
            dcLayers[Object.keys(dcLayers)[0]].layerOptions.opacity = (dcOpacity);
          }
          var layers = _.merge({}, dcLayers, overlayLayers);
          renderTileLayers(layers, 'overlays'); // Render in overlays layer of leaflet
        });
      }
    };

    Utils.Ctrl.watch($attrs, $scope, vm, 'datacaptures', function (dcs) {
      renderOverlaysDataCaptures(vm.overlays, dcs, $scope);
    });

    Utils.Ctrl.watch($attrs, $scope, vm, 'overlays', function (overlays) {
      renderOverlaysDataCaptures(overlays, vm.datacaptures, $scope);
    });

    Utils.Ctrl.watch($attrs, $scope, vm, 'annotations', renderGeoJSON);
    Utils.Ctrl.watch($attrs, $scope, vm, 'measurements', renderGeoJSON);

    Utils.Ctrl.watch($attrs, $scope, vm, 'location', function (location) {
      // map controller gets null data on first load
      if (location) {
        vm.center = location;
      }
    }, 'collection');


    // If mission changes.. enable/renable deep watchers
    // NOTE: Utils.Ctrl automatically takes care of cleaning up the previous watchers
    Utils.Ctrl.watch($attrs, $scope, vm, 'mission', function () {
      // Utils.Ctrl.watch($attrs, $scope, vm, 'mission.bases', renderGeoJSON, 'collection');
      Utils.Ctrl.watch($attrs, $scope, vm, 'mission.obstacles', renderGeoJSON, 'collection');
      Utils.Ctrl.watch($attrs, $scope, vm, 'mission.zones', renderGeoJSON, 'collection');
      Utils.Ctrl.watch($attrs, $scope, vm, 'mission.photos', renderGeoJSON, 'collection');
      Utils.Ctrl.watch($attrs, $scope, vm, 'mission.gcps', renderGeoJSON, 'collection');
      Utils.Ctrl.watch($attrs, $scope, vm, 'mission.chps', renderGeoJSON, 'collection');
    });

    Utils.Ctrl.watch($attrs, $scope, vm, 'photoset', renderPhotoset);

    // ## Watchers --- Angular Leaflet ---

    Utils.Ctrl.watch($attrs, $scope, vm, 'center', function (center) {
      vm.location = center;
    }, 'collection');

  }
}

MapController.$inject = ['$attrs', '$scope', 'sbMapRegistry', 'sbMapUtils', '$rootScope', '$timeout'];
module.exports = MapController;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/map/src/app/MapController.js